/* eslint-disable react/require-default-props */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { connect } from 'react-redux';
import { compose } from 'ramda';
import { get } from 'lodash';
import ConnectedHeader from '../components/Global/Header';
import Footer from '../components/Global/Footer';
import { load as loadAuth } from '../stores/auth';
import { load as loadFeatures } from '../stores/features';
import config from '../../config/config';
import safelyHoistStatics from '../helpers/safelyHoistStatics';
import withoutAppBanner from '../helpers/config';
import withQueryParams from './withQueryParams';
import withAnalytics from '../components/Decorators/withAnalytics';
import { toSnakeCase } from '../api/caseConverter';
import { setUser } from '../helpers/BugsnagClient';
import BrowserContext from '../context/BrowserContext';
import UserContext from '../context/UserContext';
import BrandContext from '../context/BrandContext';
import GlobalErrorBoundary from './GlobalErrorBoundary';
import { resetBrand } from '../stores/brand';

const getHelmetConfiguration = pathname => {
  const conf = pathname === '/search' ? withoutAppBanner(config) : config;
  return conf.app.head;
};

class App extends Component {
  static fetchData(store, match, query, fingerprint, entityId) {
    // First check auth, then pass this response to feature flagging
    return store
      .dispatch(loadAuth())
      .then(user =>
        store
          .dispatch(loadFeatures(user, fingerprint, entityId))
          .then(() => user)
      );
  }

  componentDidUpdate(prevProps) {
    const { location, brand, resetBrandActon } = this.props;

    const locationIsPartner =
      location.pathname && location.pathname.substr(0, 9) === '/partner/';
    const locationChanged = location.pathname !== prevProps.location.pathname;
    const isWhiteLabel = brand.isWhiteLabeled;

    if (isWhiteLabel && locationChanged && !locationIsPartner) {
      resetBrandActon();
    }
  }

  render() {
    const { standalone } = this.props.queryParams;
    const className = `${standalone ? 'standalone' : ''}`;

    const branch = matchRoutes(
      this.props.route.routes,
      this.props.location.pathname
    );

    // Tests to pass to matchRoutes that gives us an object with booleans on about what type of pages we are on
    // based on the metadata of the route, or its url, or parameters it has in the uri / querystring.
    // We use these to inform the global header and footer of their behaviour for these pages.
    const globalModifiers = {
      searchResults: ({ route }) =>
        route.meta && route.meta.routeType === 'search',
      checkout: ({ route }) =>
        route.meta && route.meta.routeType === 'checkout',
      hidePrimaryNav: ({ route }) =>
        route.meta && route.meta.headerHidePrimaryNav,
      minimalBackground: ({ route }) =>
        route.meta && route.meta.headerMinimalBackground,
      darkMobileMenu: ({ route }) =>
        route.meta && route.meta.headerDarkMobileMenu,
      hideFooter: ({ route }) => route.meta && route.meta.hideFooter
    };
    Object.keys(globalModifiers).forEach(key => {
      const test = globalModifiers[key];
      globalModifiers[key] = branch.some(test);
    });

    return (
      <GlobalErrorBoundary>
        <BrandContext.Provider value={this.props.brand}>
          <UserContext.Provider value={this.props.user}>
            <BrowserContext.Provider value={this.props.browser}>
              <div className={className}>
                <div id="portal" />
                {/* <FullStory /> TODO: FEND-1174 moves this behind a feature flag */}
                <Helmet
                  {...getHelmetConfiguration(this.props.location.pathname)}
                />
                {!standalone && (
                  <ConnectedHeader
                    user={this.props.user}
                    theme={globalModifiers}
                  />
                )}
                <GlobalErrorBoundary>
                  {renderRoutes(this.props.route.routes)}
                </GlobalErrorBoundary>
                {!standalone && !globalModifiers.hideFooter && <Footer />}
              </div>
            </BrowserContext.Provider>
          </UserContext.Provider>
        </BrandContext.Provider>
      </GlobalErrorBoundary>
    );
  }
}

App.propTypes = {
  user: PropTypes.object,
  brand: PropTypes.object,
  browser: PropTypes.object,
  location: PropTypes.object,
  route: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  queryParams: PropTypes.object.isRequired,
  resetBrandActon: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  user: state.auth.user,
  browser: state.browser,
  brand: state.brand
});

const mapDispatchToProps = {
  resetBrandActon: resetBrand
};

const valueObservers = {
  bookingCount: [
    state => get(state, 'auth.user.bookingSummary.totalBookingCount', 0),
    ({ state, analytics }) => {
      if (state && state.auth) {
        const defaultBookingSummary = {
          latestBookingDate: null,
          firstBookingDate: null,
          totalAppBookingsMade: 0,
          totalBookingsMade: 0
        };

        const { user } = state.auth;

        const defaultProperties = user
          ? user.bookingSummary
          : defaultBookingSummary;

        analytics.setDefaultProperties(toSnakeCase(defaultProperties));
      }
    }
  ],
  userId: [
    state => get(state, 'auth.user.id', null),
    ({ state, analytics }) => {
      if (state && state.auth) {
        const { user } = state.auth;

        if (user) {
          analytics.identify(user.id);

          if (__CLIENT__ && 'exponea' in window) {
            window.exponea.identify(user.id);
          }
        }

        setUser(user);

        const isLoggedIn = Boolean(user);

        analytics.setDefaultProperties({
          is_logged_in: isLoggedIn
        });
      }
    }
  ],
  features: [
    state => get(state, 'features.features', []),
    ({ state, analytics }) => {
      if (state && state.features.features.length > 0) {
        const properties = {};
        state.features.features.forEach(feature => {
          properties[`flag_enabled_${feature.key}`] = feature.enabled;
          properties[`flag_variant_${feature.key}`] = feature.variantKey;
        });

        analytics.setDefaultProperties(properties);
      }
    }
  ]
};

const appComposition = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withQueryParams,
  withAnalytics(valueObservers, {})
)(App);

export default hot(module)(safelyHoistStatics(appComposition, App));
